// Gemini clip analysis → the app's `video_clips[]` contract. Runs in the browser with
// the user's own API key (generativelanguage.googleapis.com accepts browser calls; the
// key is the user's and never touches our server). Pin the model; never *-latest.

import { GoogleGenerativeAI } from '@google/generative-ai';
import { CONFIG } from '../../config.js';
import { getKey, KEY_NAMES } from '../keys.js';
import { generateWithFallback } from './geminiModels.js';

function buildPrompt({ transcript, customPrompt, numCuts, maxDuration, language }) {
  const base = customPrompt && customPrompt.trim()
    ? customPrompt.trim()
    : `You are a viral short-form video editor (TikTok/Reels/Shorts) for Indonesian audiences. ` +
      `From the timestamped transcript, select the ${numCuts || 5} BEST self-contained moments to clip. ` +
      `Each clip must be a complete thought with a strong hook, ${maxDuration || 60}s max.`;

  return `${base}

Return ONLY valid JSON (no markdown fence), an object of this exact shape:
{
  "video_clips": [
    {
      "clip_id": "1",
      "start": <seconds number>,
      "end": <seconds number>,
      "topic": "<short title>",
      "reason": "<why this clips well>",
      "hook": "<punchy on-screen headline, ${language === 'en' ? 'English' : 'Bahasa Indonesia'}, max 8 words>"
    }
  ]
}

Rules: start/end are seconds (numbers), end-start <= ${maxDuration || 60}, ordered by virality, no overlaps.

TRANSCRIPT:
${transcript}`;
}

// Robustly pull a JSON object out of an LLM response.
function extractJson(text) {
  let t = text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  try { return JSON.parse(t); } catch { /* fall through */ }
  const first = t.indexOf('{');
  const last = t.lastIndexOf('}');
  if (first !== -1 && last > first) {
    try { return JSON.parse(t.slice(first, last + 1)); } catch { /* fall through */ }
  }
  return null;
}

// opts: { segments?, transcriptText?, customPrompt?, numCuts?, maxDuration?, language?, videoTitle? }
export async function analyzeForClips(opts = {}) {
  const key = getKey(KEY_NAMES.GEMINI) || opts.apiKey;
  if (!key) throw new Error('Gemini API key required. Add it in Settings.');

  const transcript = opts.transcriptText
    || (opts.segments || []).map((s) => `[${Math.round(s.start)}s] ${s.text}`).join('\n')
    || '(no transcript available — infer pacing from title)';

  const genAI = new GoogleGenerativeAI(key);
  const prompt = buildPrompt({
    transcript,
    customPrompt: opts.customPrompt,
    numCuts: opts.numCuts,
    maxDuration: opts.maxDuration,
    language: opts.language,
  });

  // Use the user's picked model, preflighted against the key's accessible models
  // (ListModels, cached 10 min) and walked through CONFIG.GEMINI_FALLBACKS on
  // model-level errors (404 / not found / not supported). Key-level errors throw
  // fast with a clear message — see geminiModels.js.
  const wanted = (opts.model && String(opts.model).trim()) || CONFIG.GEMINI_MODEL;
  const { result } = await generateWithFallback(key, wanted, (modelId) =>
    genAI.getGenerativeModel({ model: modelId }).generateContent(prompt)
  );
  const parsed = extractJson(result.response.text());
  if (!parsed || !Array.isArray(parsed.video_clips)) {
    throw new Error('Gemini returned an unparseable analysis. Try again.');
  }

  // Normalize: ensure numeric start/end, string clip_id, clamp duration.
  const video_clips = parsed.video_clips.map((c, i) => {
    const start = Number(c.start) || 0;
    let end = Number(c.end) || start + (opts.maxDuration || 60);
    if (end <= start) end = start + (opts.maxDuration || 60);
    return {
      clip_id: String(c.clip_id ?? i + 1),
      start, end,
      topic: c.topic || `Clip ${i + 1}`,
      reason: c.reason || '',
      hook: c.hook || c.topic || '',
    };
  });

  return { video_clips, source_info: { video_title: opts.videoTitle || 'Qlipper Web' } };
}
